import { memo, useRef, useEffect } from 'react';
import { Resizable } from 'react-resizable';
import { useMemoizedFn } from './hooks/useMemoizedFn.js';
import { useSafeState } from './hooks/useSafeState.js';
import { isString, isNumber } from './utils/index.js';
import { clsx } from './utils/clsx.js';
import { NameSpace } from './utils/constant.js';
import { jsx, jsxs } from 'react/jsx-runtime';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
function resolveThWidthByColgroup(thElement) {
  var _a, _b;
  const tableElement = thElement.closest("table");
  if (tableElement) {
    const colgroup = tableElement.querySelector("colgroup");
    const cols = colgroup == null ? void 0 : colgroup.querySelectorAll("col");
    const rows = ((_a = tableElement.querySelector("thead")) == null ? void 0 : _a.rows) || [];
    const cellPositions = [];
    for (let i = 0; i < rows.length; i++) {
      const cells2 = rows[i].cells;
      let colIndex = 0;
      for (let j = 0; j < cells2.length; j++) {
        const cell = cells2[j];
        const rowspan = cell.rowSpan || 1;
        const colspan = cell.colSpan || 1;
        while (cellPositions[i] && cellPositions[i][colIndex]) {
          colIndex++;
        }
        for (let r = 0; r < rowspan; r++) {
          for (let c = 0; c < colspan; c++) {
            if (!cellPositions[i + r]) {
              cellPositions[i + r] = [];
            }
            cellPositions[i + r][colIndex + c] = cell;
          }
        }
        colIndex += colspan;
      }
    }
    const cells = cellPositions[cellPositions.length - 1];
    const colindex = cells.indexOf(thElement);
    if (colindex !== -1) {
      const colWidth = (_b = cols == null ? void 0 : cols[colindex]) == null ? void 0 : _b.clientWidth;
      if (isNumber(colWidth)) {
        return colWidth;
      }
    }
  }
}
function ResizableHeader(props) {
  const _a = props, {
    width,
    minWidth,
    maxWidth,
    resizable,
    hide,
    _onResizeEnd,
    onResizeStart,
    onResizeEnd,
    onColChange,
    shouldRender,
    className,
    style,
    onClick,
    children,
    rowSpan,
    colSpan,
    title,
    scope
  } = _a, rest = __objRest(_a, [
    "width",
    "minWidth",
    "maxWidth",
    "resizable",
    "hide",
    "_onResizeEnd",
    "onResizeStart",
    "onResizeEnd",
    "onColChange",
    "shouldRender",
    "className",
    "style",
    "onClick",
    "children",
    "rowSpan",
    "colSpan",
    "title",
    "scope"
  ]);
  const [resizeWidth, setResizeWidth] = useSafeState(0);
  const [colWidth, setColWidth] = useSafeState(0);
  const thRef = useRef(null);
  const onColWidthChanged = useMemoizedFn((callback) => {
    const fn = () => {
      if (thRef.current) {
        const colWidth2 = resolveThWidthByColgroup(thRef.current);
        if (colWidth2) {
          setColWidth(colWidth2);
          setResizeWidth(colWidth2);
          callback == null ? void 0 : callback(colWidth2);
        }
      }
    };
    if (window.requestIdleCallback) {
      window.requestIdleCallback(() => {
        fn();
      });
    } else {
      setTimeout(() => {
        fn();
      }, 1);
    }
  });
  useEffect(() => {
    if (width) {
      onColWidthChanged();
    }
  }, [width, shouldRender]);
  if (hide) {
    return null;
  }
  if (!width || Number.isNaN(Number(width)) || resizable === false) {
    return /* @__PURE__ */ jsx(
      "th",
      __spreadProps(__spreadValues({}, rest), {
        "data-resizable-col": "false",
        style,
        className,
        onClick,
        rowSpan,
        colSpan,
        ref: thRef,
        children: /* @__PURE__ */ jsx("span", { title, children })
      })
    );
  }
  const setBodyStyle = (active) => {
    document.body.style.userSelect = active ? "none" : "";
    document.body.style.pointerEvents = active ? "none" : "";
    document.documentElement.style.cursor = active ? "col-resize" : "";
  };
  const onStart = ({}, data) => {
    if (resizeWidth >= maxWidth || resizeWidth <= minWidth) return;
    setResizeWidth(data.size.width);
    onResizeStart == null ? void 0 : onResizeStart(data.size.width);
  };
  const onSelfResize = ({}, data) => {
    setBodyStyle(true);
    setResizeWidth(data.size.width);
  };
  const onStop = () => {
    setBodyStyle(false);
    if (resizeWidth <= 0) return;
    let w = resizeWidth;
    if (w >= maxWidth) {
      w = maxWidth > width ? maxWidth : width;
    } else if (w <= minWidth) {
      w = minWidth < width ? minWidth : width;
    }
    _onResizeEnd(w);
    onResizeEnd == null ? void 0 : onResizeEnd(w);
    onColWidthChanged();
  };
  const isSimpleChildren = () => {
    var _a2, _b;
    if (Array.isArray(children)) {
      const lastChild = children[children.length - 1];
      if (lastChild) {
        return isString(lastChild) || ((_a2 = lastChild.props) == null ? void 0 : _a2.ellipsis) || isString((_b = lastChild.props) == null ? void 0 : _b.label);
      }
    }
    return false;
  };
  return /* @__PURE__ */ jsxs(
    "th",
    {
      ref: thRef,
      scope,
      className: clsx(NameSpace.CSS, className),
      style: __spreadProps(__spreadValues({}, style), {
        overflow: "unset"
      }),
      "data-resizable-col": "true",
      onClick,
      rowSpan,
      colSpan,
      children: [
        /* @__PURE__ */ jsx(
          Resizable,
          {
            className: `${NameSpace.CSS}__content`,
            width: resizeWidth,
            minConstraints: [Math.min(minWidth, colWidth), 0],
            maxConstraints: [Math.max(maxWidth, colWidth), 0],
            height: 0,
            handle: /* @__PURE__ */ jsx(
              "div",
              {
                className: `${NameSpace.CSS}__handler`,
                onClick: (e) => {
                  e.stopPropagation();
                },
                children: /* @__PURE__ */ jsx("div", { className: `${NameSpace.CSS}__handler__line` })
              }
            ),
            draggableOpts: { enableUserSelectHack: false },
            onResizeStart: onStart,
            onResize: onSelfResize,
            onResizeStop: onStop,
            children: /* @__PURE__ */ jsx("div", { style: { width: resizeWidth, height: "100%" } })
          }
        ),
        /* @__PURE__ */ jsx("div", __spreadProps(__spreadValues({}, rest), { className: `${NameSpace.CSS}__title${isSimpleChildren() ? "--ellipsis" : ""}`, children }))
      ]
    }
  );
}
var ResizableHeader_default = memo(ResizableHeader);

export { ResizableHeader_default as default };
